import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCart } from '../context/CartContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  getDeliveryProviders,
  getSavedPickupPoints,
  selectSavedPickupPoint,
  calculateDelivery,
} from '../api';
import {
  DELIVERY_PROVIDERS,
  deliveryProviderLabel,
} from '../constants/deliveryProviders';
import Icon from '../components/Icon';
import ProductImage from '../components/ProductImage';
import api from '../api';
import {
  createOrdersAndPay,
  CheckoutAuthRequiredError,
  pendingCustomerCheckout,
} from '../services/checkoutService';
import { getCustomer, saveCustomer } from '../services/customerService';
import YandexPickupMap from '../components/YandexPickupMap';
import CheckoutRecovery from '../components/CheckoutRecovery';
import PaymentChoice from '../components/PaymentChoice';
import useCustomerCollections from '../hooks/useCustomerCollections';

function CheckoutDeliveryPage() {
  const { selectedItems: items, selectedTotalPrice: totalPrice } = useCart();
  const collections = useCustomerCollections();
  const navigate = useNavigate();
  const location = useLocation();
  const [previousOrder, setPreviousOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [buyerName, setBuyerName] = useState('');
  const [checkoutAccess, setCheckoutAccess] = useState({ allowed: false, loading: true });
  const [pending, setPending] = useState(pendingCustomerCheckout);
  const [provider, setProvider] = useState('YANDEX');
  const [providers, setProviders] = useState(DELIVERY_PROVIDERS);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState('');
  const selectionVersion = useRef(0);
  const submitActive = useRef(false);
  const selectedProvider = providers.find((item) => item.id === provider);
  const providerAvailable =
    selectedProvider?.available === true &&
    !providersLoading &&
    !providersError;
  const [savedPoints, setSavedPoints] = useState([]);
  const [pointsLoaded, setPointsLoaded] = useState(false);
  const [pointsError, setPointsError] = useState('');
  const [pointsRetry, setPointsRetry] = useState(0);
  const [savingPoint, setSavingPoint] = useState(false);
  const pointSelectionActive = useRef(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [point, setPoint] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [savedCardId, setSavedCardId] = useState('');
  const availableSavedPoints = savedPoints.filter(p => p.provider === provider);
  const itemPayload = useMemo(
    () =>
      items.map((item) => ({
        variantId: item.selectedVariantId,
        quantity: item.quantity || 1,
      })),
    [items]
  );
  const delivery = useMemo(
    () => ({
      provider,
      method: 'PICKUP_POINT',
      city: point?.city || '',
      pickupPoint: point
        ? {
            id: point.providerId || point.id,
            name: point.name,
            address: point.address?.full_address || point.address,
            latitude: point.latitude ?? null,
            longitude: point.longitude ?? null,
          }
        : null,
    }),
    [provider, point]
  );

  useEffect(() => {
    const c = getCustomer();
    if (c) { setCustomer(c); setBuyerName(c.name || ''); }
  }, []);
  useEffect(() => {
    if (!customer?.phone) return undefined;
    let active = true;
    api.get('/api/payment/checkout-config').then(res => { if (active) setCheckoutAccess({ ...res.data, loading: false }); })
      .catch(() => { if (active) setCheckoutAccess({ allowed: false, loading: false, message: 'Не удалось проверить доступность оплаты. Обновите страницу.' }); });
    return () => { active = false; };
  }, [customer?.phone]);
  useEffect(() => {
    if (!customer?.phone || !checkoutAccess.allowed || pending) return undefined;
    let active = true;
    setProvidersLoading(true);
    setProvidersError('');
    getDeliveryProviders()
      .then((res) => {
        if (!active) return;
        setProviders(
          DELIVERY_PROVIDERS.map((item) => ({
            ...item,
            ...res.data.providers?.find(
              (available) => available.id === item.id
            ),
          }))
        );
      })
      .catch(() => {
        if (active)
          setProvidersError(
            'Не удалось получить службы доставки. Обновите страницу или попробуйте позже.'
          );
      })
      .finally(() => {
        if (active) setProvidersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customer?.phone, checkoutAccess.allowed, pending]);
  useEffect(() => {
    if (!customer?.phone || !providerAvailable || pending)
      return undefined;
    let active = true;
    const version = selectionVersion.current;
    setPointsError(''); setPointsLoaded(false);
    getSavedPickupPoints()
      .then((res) => {
        if (active && version === selectionVersion.current) {
          const records = res.data.points || [];
          setSavedPoints(records);
          setPoint(records.filter(p => p.provider === provider).at(-1) || null);
          setPointsLoaded(true);
        }
      })
      .catch(() => {
        if (active && version === selectionVersion.current) {
          setPointsError('Не удалось загрузить сохранённые пункты. Попробуйте снова.');
        }
      });
    return () => {
      active = false;
    };
  }, [provider, customer?.phone, providerAvailable, pending, pointsRetry]);
  useEffect(() => {
    setQuote(null);
    setQuoteLoading(false);
    if (
      !customer?.phone ||
      !providerAvailable ||
      savingPoint ||
      !itemPayload.length ||
      !itemPayload.every((item) => item.variantId) ||
      !point
    )
      return undefined;
    let active = true;
    const version = selectionVersion.current;
    setQuoteLoading(true);
    setDeliveryError('');
    calculateDelivery({ items: itemPayload, delivery })
      .then((res) => {
        if (!active || version !== selectionVersion.current) return;
        if (res.data.provider !== provider)
          throw new Error('Delivery provider mismatch');
        setQuote(res.data);
      })
      .catch(() => {
        if (active && version === selectionVersion.current)
          setDeliveryError(
            `Расчёт ${deliveryProviderLabel(provider)} сейчас недоступен. Ваш заказ ещё не создан — попробуйте другую службу или вернитесь позже.`
          );
      })
      .finally(() => {
        if (active && version === selectionVersion.current)
          setQuoteLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    customer?.phone,
    provider,
    providerAvailable,
    itemPayload,
    delivery,
    point,
    savingPoint,
  ]);

  const changeProvider = (next) => {
    if (next === provider) return;
    selectionVersion.current += 1;
    setProvider(next);
    setMapOpen(false);
    setPoint(null);
    setQuote(null);
    setQuoteLoading(false);
    setDeliveryError('');
    setPaymentError('');
  };

  const selectPoint = async providerId => {
    if (pointSelectionActive.current) return;
    pointSelectionActive.current = true;
    selectionVersion.current += 1;
    setSavingPoint(true); setPointsError(''); setQuote(null);
    try {
      const { data } = await selectSavedPickupPoint(providerId, provider);
      setSavedPoints(data.points); setPoint(data.selected);
      const cached = getCustomer();
      if (cached?.phone === customer.phone) { try { saveCustomer({ ...cached, savedPoints: data.points }); } catch { /* Server persistence already succeeded. */ } }
      setMapOpen(false);
    } catch (error) {
      setPointsError(error.response?.data?.error || 'Не удалось сохранить пункт. Попробуйте выбрать его ещё раз.');
      throw error;
    } finally { pointSelectionActive.current = false; setSavingPoint(false); }
  };

  const handleSubmit = async () => {
    if (loading || savingPoint || submitActive.current || !checkoutAccess.allowed) return;
    if (!buyerName.trim()) { setPaymentError('Укажите ваше имя'); return; }
    if (
      !providerAvailable ||
      !quote ||
      quote.provider !== provider ||
      quoteLoading
    )
      return;
    submitActive.current = true;
    setLoading(true);
    setPaymentError('');
    try {
      const paymentUrl = await createOrdersAndPay(
        items,
        { name: buyerName.trim(), phone: customer?.phone || '' },
        delivery,
        false,
        Math.round((totalPrice + quote.price) * 100),
        savedCardId
      );
      window.location.href = paymentUrl;
    } catch (error) {
      setPending(pendingCustomerCheckout());
      if (error instanceof CheckoutAuthRequiredError) {
        navigate('/profile', { state: { from: '/checkout' } });
        return;
      }
      setPaymentError(
        error.response?.data?.error ||
          error.message ||
          'Не удалось оформить заказ. Проверьте данные и повторите попытку.'
      );
    } finally {
      submitActive.current = false;
      setLoading(false);
    }
  };

  if (process.env.REACT_APP_BANK_REVIEW_MODE === 'true' && process.env.REACT_APP_CUSTOMER_CHECKOUT_ENABLED !== 'true') {
    return (
      <div className="checkout-container">
        <h1>Оформление заказа</h1>
        <div className="checkout-notice">
          <p>
            MaxMarket готовится к открытию. Приём заказов и оплата пока
            недоступны.
          </p>
          <Link to="/contacts">Связаться с магазином</Link>
        </div>
        <Link to="/">Вернуться в каталог</Link>
      </div>
    );
  }

  if (collections.signedIn && collections.loading) return <div className="checkout-container"><h1>Оформление заказа</h1><p role="status">Загружаем выбранные товары…</p></div>;
  if (collections.signedIn && !collections.verified) return <div className="checkout-container"><h1>Оформление заказа</h1><p role="alert">{collections.error}</p><Link to="/profile">Войти в профиль</Link></div>;
  if (customer && pending) return <CheckoutRecovery pending={pending} initialError={paymentError} checkoutSource={location.state?.checkoutSource} selectedItems={items} onResolved={previous => { setPreviousOrder(previous || null); setPending(null); }} />;
  if (customer && !checkoutAccess.allowed) return (
    <div className="checkout-container"><h1>Оформление заказа</h1><p role="status">{checkoutAccess.loading ? 'Проверяем доступность оплаты…' : checkoutAccess.message}</p><Link to="/">Вернуться в каталог</Link></div>
  );

  return (
    <div className="checkout-container">
      <h2>Оформление заказа</h2>
      {previousOrder && <p className="checkout-notice" role="status">Сейчас оформляете выбранные товары из корзины. Прежний заказ с другим составом сохранён отдельно. <Link to={`/orders/${previousOrder.id}`}>Открыть прежний заказ</Link></p>}
      {mapOpen && <YandexPickupMap city={point?.city || 'Москва'} busy={savingPoint} error={pointsError} onSelect={selectPoint} onClose={() => setMapOpen(false)} />}
      {checkoutAccess.live && <p className="checkout-notice">Каталог демонстрационный. Оплата через рабочий терминал Т-Банка спишет показанную сумму.</p>}
      {!customer && (
        <div className="checkout-notice">
          <p>Войдите в профиль, чтобы выбрать доставку и оформить заказ.</p>
          <Link
            className="btn btn-primary"
            to="/profile"
            state={{ from: '/checkout' }}
          >
            Войти в профиль
          </Link>
        </div>
      )}
      {deliveryError && (
        <p className="checkout-notice checkout-notice--error" role="alert">
          {deliveryError}
        </p>
      )}
      {paymentError && (
        <p className="checkout-notice checkout-notice--error" role="alert">
          {paymentError}
        </p>
      )}
      <div className="checkout-card">
        <label htmlFor="checkout-buyer-name">Ваше имя</label>
        <input id="checkout-buyer-name" className="checkout-input" autoComplete="name" value={buyerName} onChange={e => setBuyerName(e.target.value)} maxLength={100} required />
        <h3>Выбранные товары</h3><p className="purchase-notice">Неотмеченные позиции остаются в корзине.</p>
        {items.map((item, i) => (
          <div className="checkout-item-card" key={`${item.id}-${i}`}>
            <ProductImage product={item} width={54} height={72} />
            <span>
              {item.title} × {item.quantity}
            </span>
          </div>
        ))}
        <strong>Товары: {totalPrice} ₽</strong>{quote?.packing?.mode === 'CONSOLIDATED' && <p className="buyer-notice">Одна посылка со склада MaxMarket · {quote.packing.unitsCount} шт. Доставка рассчитана за общую упаковку.</p>}
      </div>
      <div className="checkout-card">
        <h3>Служба доставки</h3>
        <fieldset
          className="checkout-provider-options"
          aria-label="Служба доставки"
        >
          {providers.map((item) => (
            <label
              key={item.id}
              className={`checkout-provider-option ${provider === item.id ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="delivery-provider"
                value={item.id}
                checked={provider === item.id}
                disabled={savingPoint || loading}
                onChange={() => changeProvider(item.id)}
              />
              <span
                className={`checkout-provider-mark checkout-provider-mark--${item.id.toLowerCase()}`}
                aria-hidden="true"
              >
                {item.id === 'YANDEX' ? 'Я' : 'O'}
              </span>
              <span>
                <strong>{item.name}</strong>
                <small>
                  {!customer
                    ? 'Войдите, чтобы проверить доступность'
                    : providersLoading
                      ? 'Проверяем доступность…'
                      : item.available
                        ? 'В пункт выдачи'
                        : 'Пока недоступна'}
                </small>
              </span>
              <Icon
                name={provider === item.id ? 'check' : 'chevron'}
                size={18}
              />
            </label>
          ))}
        </fieldset>
        {providersError && (
          <p className="checkout-notice checkout-notice--error" role="alert">
            {providersError}
          </p>
        )}
        {customer &&
          !providersLoading &&
          !providersError &&
          !providerAvailable && (
            <p className="checkout-provider-unavailable" role="status">
              {selectedProvider?.message ||
                'Эта служба доставки пока недоступна для оформления.'}
            </p>
          )}
        <section className="pickup-points" aria-labelledby="checkout-pickup-title">
          <h3 id="checkout-pickup-title">Получение в пункте выдачи</h3>
          {providerAvailable && !pointsLoaded && !pointsError && <p role="status">Загружаем ваши пункты выдачи…</p>}
          {pointsError && !mapOpen && <p role="alert" className="checkout-notice checkout-notice--error">{pointsError}</p>}
          {providerAvailable && !pointsLoaded && pointsError && <button type="button" className="btn btn-outline" onClick={() => setPointsRetry(value => value + 1)}>Повторить загрузку</button>}
          {!!availableSavedPoints.length && <fieldset className="pickup-points-list" disabled={savingPoint || loading || !providerAvailable}>
            <legend>Ваши пункты выдачи</legend>
            {[...availableSavedPoints].reverse().map(saved => <label key={saved.id} className={`pickup-point-card ${point?.id === saved.id ? 'is-selected' : ''}`}>
              <input type="radio" name="saved-pickup-point" checked={point?.id === saved.id} onChange={() => selectPoint(saved.id).catch(() => {})} />
              <span><strong>{saved.address}</strong><small>{saved.name}{point?.id === saved.id ? ' · Выбран для заказа' : ''}</small></span>
            </label>)}
          </fieldset>}
          {savingPoint && !mapOpen && <p role="status">Проверяем выбранный пункт…</p>}
          <button type="button" className="btn btn-outline" disabled={!providerAvailable || !pointsLoaded || savingPoint || loading} onClick={() => { setPointsError(''); setMapOpen(true); }}>
            {availableSavedPoints.length ? 'Выбрать другой пункт на карте' : 'Выбрать пункт на карте'}
          </button>
          {!availableSavedPoints.length && pointsLoaded && <p>Выбранный пункт сохранится. Позже можно добавить ещё один или переключиться между сохранёнными.</p>}
        </section>
      </div>
      <div className="checkout-card">
        <p className="checkout-selected-provider">
          {deliveryProviderLabel(provider)}
        </p>
        <p>
          Стоимость доставки:{' '}
          <strong>
            {quoteLoading
              ? 'Рассчитываем…'
              : quote
                ? `${quote.price} ₽`
                : 'Не рассчитана'}
          </strong>
        </p>
        {quote?.deliveryDays ? <p>Срок: {quote.deliveryDays} дн.</p> : null}
        <strong>
          {quote
            ? `К оплате: ${totalPrice + quote.price} ₽`
            : 'Итог появится после расчёта доставки'}
        </strong>
      </div>
      {customer && <PaymentChoice value={savedCardId} onChange={setSavedCardId} disabled={loading || savingPoint} />}
      <button
        className="checkout-btn-primary"
        disabled={
          loading ||
          savingPoint ||
          !providerAvailable ||
          !quote ||
          quote.provider !== provider ||
          quoteLoading
        }
        onClick={handleSubmit}
      >
        {loading ? 'Создание платежа…' : savedCardId ? 'Оплатить сохранённой картой' : 'Перейти к оплате'}
      </button>
    </div>
  );
}

export default CheckoutDeliveryPage;
